import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import type { FormField, FieldType, PathHop } from '@/features/application/types/forms';
import * as formBuilderApi from '@/features/form-builder/api/form-builder.services';
import { useMappingsStore } from '@/features/form-builder/store/mappings.store';

export type BankAnalysisMetricField = FormField & {
  metricId: string;
  sourceIndex?: number;
};

type BankAnalysisMappingRow = {
  metricId: string;
  sourceIndex?: number;
  path: PathHop[];
  fieldApiName: string;
  fieldLabel?: string;
  isPathConfirmed?: boolean;
};

export type BankAnalysisMetricSelection = {
  metricId: string;
  sourceIndex?: number;
};

interface BankAnalysisMappingState {
  loadedForMappingId: string | null;
  isLoading: boolean;
  metrics: formBuilderApi.BankAnalysisMetricDescriptor[];
  metricFieldsByKey: Record<string, BankAnalysisMetricField>;
  activeMetricSelection: BankAnalysisMetricSelection | null;
  statementPeriodCount: number;

  setActiveMetricSelection: (selection: BankAnalysisMetricSelection | null) => void;
  clearActiveMetric: () => void;
  addStatementPeriod: () => void;

  loadForMappingId: (mappingId: string) => Promise<void>;
  updateMetricField: (selection: BankAnalysisMetricSelection, updates: Partial<BankAnalysisMetricField>) => void;
  saveBankAnalysisMappings: (mappingId: string) => Promise<void>;
}

// Purpose: Convert metric registry value type into a form-builder FieldType.
const metricValueTypeToFieldType = (valueType: string): FieldType => {
  if (valueType === 'number') return 'number';
  if (valueType === 'boolean') return 'checkbox';
  return 'text';
};

// Purpose: Build a stable composite key for metric selection and storage.
const makeMetricKey = (metricId: string, sourceIndex?: number): string => {
  return `${metricId}::${Number.isInteger(sourceIndex) ? sourceIndex : 'na'}`;
};

// Purpose: Create a UI field model for a bank analysis metric mapping row.
const createMetricFieldModel = (params: {
  metric: formBuilderApi.BankAnalysisMetricDescriptor;
  existing?: BankAnalysisMappingRow;
  sourceIndex?: number;
}): BankAnalysisMetricField => {
  const { metric, existing, sourceIndex } = params;
  const fieldType = metricValueTypeToFieldType(metric.valueType);
  const fieldId = `bank-analysis:${makeMetricKey(metric.id, sourceIndex)}`;

  return {
    metricId: metric.id,
    sourceIndex,
    id: fieldId,
    type: fieldType,
    label: metric.label,
    displayName: metric.label,
    value: null,
    required: false,
    permissions: { canEdit: [1], canView: [1] },
    // Hydrated mapping values (if any)
    path: Array.isArray(existing?.path) ? existing.path : [],
    fieldApiName: existing?.fieldApiName || '',
    fieldLabel: existing?.fieldLabel || '',
    isPathConfirmed: Boolean(existing?.isPathConfirmed),
  } as BankAnalysisMetricField;
};

export const useBankAnalysisMappingStore = create<BankAnalysisMappingState>((set, get) => ({
  loadedForMappingId: null,
  isLoading: false,
  metrics: [],
  metricFieldsByKey: {},
  activeMetricSelection: null,
  statementPeriodCount: 1,

  setActiveMetricSelection: (selection) => set({ activeMetricSelection: selection }),
  clearActiveMetric: () => set({ activeMetricSelection: null }),

  addStatementPeriod: () => {
    const state = get();
    const newIndex = state.statementPeriodCount;
    const repeatableMetrics = state.metrics.filter((m) => Boolean(m.repeatable));
    if (repeatableMetrics.length === 0) {
      set({ statementPeriodCount: state.statementPeriodCount + 1 });
      return;
    }

    set((prev) => {
      const nextFields = { ...prev.metricFieldsByKey };
      for (const metric of repeatableMetrics) {
        const key = makeMetricKey(metric.id, newIndex);
        if (!nextFields[key]) {
          nextFields[key] = createMetricFieldModel({ metric, existing: undefined, sourceIndex: newIndex });
        }
      }
      return {
        ...prev,
        statementPeriodCount: prev.statementPeriodCount + 1,
        metricFieldsByKey: nextFields,
      };
    });
  },

  loadForMappingId: async (mappingId) => {
    const state = get();
    if (state.loadedForMappingId === mappingId && state.metrics.length > 0) {
      return;
    }

    set({ isLoading: true });
    try {
      const [registry, mappingDetail] = await Promise.all([
        formBuilderApi.fetchBankAnalysisMetrics(),
        useMappingsStore.getState().fetchMappingDetails(mappingId),
      ]);

      const existingRows: BankAnalysisMappingRow[] =
        (mappingDetail?.bank_analysis_mappings as any)?.mappings || [];

      const maxSourceIndex = existingRows
        .map((r) => (Number.isInteger(r?.sourceIndex) ? (r.sourceIndex as number) : -1))
        .reduce((best, current) => (current > best ? current : best), -1);
      const statementPeriodCount = Math.max(1, maxSourceIndex + 1);

      const existingByKey = new Map(
        existingRows.map((r) => [
          makeMetricKey(r.metricId, r.sourceIndex),
          r,
        ])
      );

      const metricFieldsByKey: Record<string, BankAnalysisMetricField> = {};

      for (const metric of registry.metrics) {
        if (metric.repeatable) {
          for (let i = 0; i < statementPeriodCount; i += 1) {
            const existing = existingByKey.get(makeMetricKey(metric.id, i));
            metricFieldsByKey[makeMetricKey(metric.id, i)] = createMetricFieldModel({
              metric,
              existing,
              sourceIndex: i,
            });
          }
        } else {
          const existing = existingByKey.get(makeMetricKey(metric.id, undefined));
          metricFieldsByKey[makeMetricKey(metric.id, undefined)] = createMetricFieldModel({
            metric,
            existing,
            sourceIndex: undefined,
          });
        }
      }

      set({
        loadedForMappingId: mappingId,
        metrics: registry.metrics,
        metricFieldsByKey,
        statementPeriodCount,
      });
    } catch (error) {
      logger.error('BankAnalysisMappingStore', 'Failed to load bank analysis mapping registry', {
        error,
        mappingId,
      });
      toast.error('Failed to load bank analysis metrics.');
    } finally {
      set({ isLoading: false });
    }
  },

  updateMetricField: (selection, updates) => {
    set((state) => {
      const key = makeMetricKey(selection.metricId, selection.sourceIndex);
      const current = state.metricFieldsByKey[key];
      if (!current) return state;
      return {
        ...state,
        metricFieldsByKey: {
          ...state.metricFieldsByKey,
          [key]: { ...current, ...updates },
        },
      };
    });
  },

  saveBankAnalysisMappings: async (mappingId) => {
    const state = get();
    const rows: formBuilderApi.BankAnalysisMetricPathMapping[] = Object.values(state.metricFieldsByKey)
      .filter(
        (f): f is BankAnalysisMetricField & { fieldApiName: string } =>
          typeof f.fieldApiName === 'string' && f.fieldApiName.trim().length > 0
      )
      .map((f) => ({
        metricId: f.metricId,
        sourceIndex: f.sourceIndex,
        path: Array.isArray(f.path) ? f.path : [],
        fieldApiName: f.fieldApiName,
        fieldLabel: f.fieldLabel || undefined,
        isPathConfirmed: Boolean(f.isPathConfirmed),
      }));

    set({ isLoading: true });
    try {
      const resp = await formBuilderApi.saveAndTestBankAnalysisMappings({
        mappingId,
        bankAnalysisMappings: rows,
      });

      if ((resp as any)?.validationFailed) {
        toast.error('Bank analysis mapping validation failed.');
        return;
      }

      toast.success('Bank analysis mappings saved.');
      await useMappingsStore.getState().fetchMappingDetails(mappingId);
    } catch (error) {
      logger.error('BankAnalysisMappingStore', 'Failed to save bank analysis mappings', {
        error,
        mappingId,
      });
      toast.error('Failed to save bank analysis mappings.');
    } finally {
      set({ isLoading: false });
    }
  },
}));
